/**
 * Configuration centralisée des permissions par route.
 * Utilisé par RoutePermissionGuard pour le contrôle d'accès au niveau du routing.
 */
import type { MenuKey } from '@/types/permissions';

export interface RoutePermissionConfig {
  path: string;
  requiredPermissions?: MenuKey[];
  requireSuperAdmin?: boolean;
}

/**
 * Liste des configurations route → permissions.
 * Les routes sont évaluées par ordre de spécificité (paths les plus longs en premier).
 * Un path s'applique aussi aux sous-routes (ex. /settings couvre /settings/overview).
 */
export const routePermissions: RoutePermissionConfig[] = [
  // Achats (factures fournisseurs) : gérant et admin (permission gestion)
  { path: '/business/achats', requiredPermissions: ['gestion'] },
  // Inventaire 2023 : gérant et admin (permission gestion) — prix HTVA fournisseurs sensibles
  { path: '/inventaire-2023', requiredPermissions: ['gestion'] },
  // Inventaire (stock/prix) : gérant et admin (permission gestion)
  { path: '/inventaire', requiredPermissions: ['gestion'] },
  // Commandes fournisseurs : gérant et admin (permission gestion)
  { path: '/business/commandes-fournisseurs', requiredPermissions: ['gestion'] },
  { path: '/business/commandes-en-attente', requiredPermissions: ['gestion'] },
  // Paiements fournisseurs : gérant et admin (permission gestion)
  { path: '/business/paiements-fournisseurs', requiredPermissions: ['gestion'] },
  { path: '/business/factures-a-payer', requiredPermissions: ['gestion'] },
  // Surveillance des suppressions d'acomptes : garde réelle = allowlist email
  // (RLS + composant), ce menu_key assure juste la cohérence du routing
  { path: '/surveillance-acomptes', requiredPermissions: ['payment_audit'] },
  // Factures fournisseurs dans Settings : super admin uniquement
  { path: '/settings/billit/purchase-invoices', requireSuperAdmin: true },
  // Settings : super admin uniquement (ordre important : règles plus spécifiques en premier)
  { path: '/settings', requireSuperAdmin: true },

  // MainLayout — routes métier critiques
  { path: '/clients', requiredPermissions: ['clients'] },
  { path: '/fournisseurs', requiredPermissions: ['listes'] },
  { path: '/articles', requiredPermissions: ['products', 'articles'] },
  { path: '/deliveries', requiredPermissions: ['deliveries', 'livraisons'] },
  { path: '/recherche', requiredPermissions: ['search'] },
  { path: '/ca-du-jour', requiredPermissions: ['ca_du_jour'] },
  { path: '/statistiques-periode', requiredPermissions: ['statistics', 'statistiques'] },
  { path: '/statistiques-vendeur', requiredPermissions: ['statistics', 'statistiques'] },
  { path: '/marketing-studio', requiredPermissions: ['marketing_studio'] },
  { path: '/devis', requiredPermissions: ['ventes'] },
  { path: '/payment-methods', requiredPermissions: ['ventes'] },
  { path: '/vendors', requiredPermissions: ['ventes'] },
  { path: '/delivery-timings', requiredPermissions: ['ventes'] },
  { path: '/service-types', requiredPermissions: ['ventes'] },
  { path: '/lift-types', requiredPermissions: ['ventes'] },
  { path: '/business/services-apres-vente', requiredPermissions: ['after_sales'] },
  { path: '/business/bons-de-commande', requiredPermissions: ['orders'] },
  { path: '/business/bons-de-commande2', requiredPermissions: ['orders2'] },
  { path: '/business/factures', requiredPermissions: ['ventes', 'comptabilite', 'factures'] },
  {
    path: '/business/factures-acomptes',
    requiredPermissions: ['payments', 'accompte_list', 'comptabilite'],
  },
  { path: '/business/notes-de-credit', requiredPermissions: ['credit_notes', 'comptabilite'] },
];

/**
 * Trouve la config applicable pour un pathname donné (match exact ou préfixe).
 * Retourne la config du path le plus spécifique qui matche.
 */
export function getRoutePermissionConfig(pathname: string): RoutePermissionConfig | undefined {
  const normalized = pathname.replace(/\/$/, '') || '/';
  const sorted = [...routePermissions].sort((a, b) => b.path.length - a.path.length);
  return sorted.find(
    config => normalized === config.path || normalized.startsWith(config.path + '/')
  );
}
